import './GridManager.css';
import {
	IonPage, IonHeader, IonToolbar, IonButtons, IonBackButton, IonTitle, IonContent, IonRow, IonLoading,
	IonButton, IonIcon, IonCard, IonText, IonCardHeader, IonCardTitle, IonCardSubtitle, IonCardContent, IonList
} from '@ionic/react';
import {arrowBack, cloudUploadOutline, downloadOutline} from 'ionicons/icons';
import React from 'react';
import {useMutation} from "@apollo/client";
import {PARSE_GRID_FILES, LOAD_NEW_GRIDS} from "../schemas/gridmanager";
import Webix from '../components/Webix';

import {
	ParseGridFilesRequest,
	ParseGridFilesResponse,
	LoadNewGridsRequest,
	LoadNewGridsResponse,
	AmsGridUnparsed
} from "@backend/types/gridmanager"

const webix: any = (window as any).webix;
const $$: any = (window as any).$$;

enum GridManagerState {
	UPLOAD_FILES = 1,
	REVIEW_GRIDS = 2
}

enum ETC {
	NONE = '',
	ENTOUCH = 'ENTOUCH',
	ASSIST = 'ASSIST',
}

// when accessed via an embedded iframe, the URL passes 'client' to indicate what ETC the user is currently on
function getEtcFromUrlParameters(): ETC {
	const currentClient = new URLSearchParams(window.location.search).get("client");
	if (currentClient === 'ent') {
		return ETC.ENTOUCH;
	} else if (currentClient === 'aw') {
		return ETC.ASSIST;
	} else {
		if (currentClient) {
			console.warn(`Client URL parameter passed in was '${currentClient}' instead of expected values [null, 'ent', 'aw']`);
		}
		return ETC.NONE;
	}
}

// if a tab name is included in the input text, focus that tab - this can highlight tabs referred to by error messages
function tryFocusTab(potentialTabNameText: string): void {
	if (!potentialTabNameText) {
		return;
	}

	const tabBar = $$('gridTabView').getTabbar();
	const tabs = tabBar.data.options.map((opt: any) => [opt.value, opt.id]);
	for (let i = 0; i < tabs.length; i++) {
		const [name, id] = tabs[i];
		if (potentialTabNameText.includes(name)) {
			tabBar.setValue(id);
			return;
		}
	}
}

const GridManager: React.FC<any> = ({embedded = false}) => {
	const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
	const etc: ETC = getEtcFromUrlParameters();

	const [webixUiConfig, setWebixUiConfig] = React.useState<any>(null);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);
	const [gridManagerState, setGridManagerState] = React.useState<GridManagerState>(GridManagerState.UPLOAD_FILES);

	const [parseGridFilesMutation] = useMutation(PARSE_GRID_FILES);
	const [loadNewGridsMutation] = useMutation(LOAD_NEW_GRIDS);

	// ApolloError needs to be stringified, otherwise it will not include details when using console.error
	const logPossibleApolloError = (error: any) => {
		if (error?.graphQLErrors !== undefined) {
			console.error(JSON.stringify(error, null, 2));
		} else {
			console.error(error);
		}
	}

	const showLoadMessage = (message: string | boolean) => {
		if (message && typeof message === 'string') {
			setLoadingMessage(message);
			setIsLoading(false);
			setIsLoading(true);
		} else {
			setLoadingMessage(DEFAULT_LOADING_MESSAGE);
			setIsLoading(false);
		}
	}

	const showWebixError = async (errorMessage: string) => {
		try {
			showLoadMessage(false);
			await webix.alert({
				title: "Error",
				text: errorMessage,
				type: "alert-error",
				id: "webixError"
			});
			const bodyWithModalboxClass = (document as any).querySelector('body.webix_modalbox_inside');
			if (bodyWithModalboxClass?.classList) {
				bodyWithModalboxClass.classList.remove('webix_modalbox_inside');
			}
		} catch (error) {
			console.error(error);
			alert(errorMessage);
		}
	}

	// the animation plays once for elements where tab_viewed=0, so setting this to null then back will replay it
	const playTabNotViewedAnimation = () => {
		document.querySelectorAll(`.webix_item_tab[tab_viewed='0']`).forEach((node: any) => {
			node.setAttribute('tab_viewed', null);
			setTimeout(() => node.setAttribute('tab_viewed', '0'), 50);
		});
	}

	const onFilesSelected = async ({target: {validity, files}}: any) => {
		if (!files.length) {
			return;
		}

		showLoadMessage('Uploading files...');
		try {
			if (validity.valid) {
				await parseGridFiles(files);
			} else {
				showWebixError('Invalid input');
			}
		} catch (error) {
			logPossibleApolloError(error);
			showWebixError(`Failed to parse loaded files:<br/><br/><i>${webix.template.escape(error)}</i>`);
		} finally {
			showLoadMessage(false);
		}
	}

	const parseGridFiles = async (files: File[]) => {
		const parseGridFilesRequest: ParseGridFilesRequest = {files};
		const parseGridFilesResult = await parseGridFilesMutation({variables: parseGridFilesRequest});
		const parseGridFilesResponse: ParseGridFilesResponse = parseGridFilesResult?.data?.parseGridFiles;
		const {success, error, grids} = parseGridFilesResponse;

		if (!success) {
			return showWebixError(
				`Error when loading template files:<br/><br/>
				 <i>${webix.template.escape(error)}</i><br/><br/>
				 Please ensure you are using the latest version of the grid template. 
				 The latest version can be retrieved from the "download template" button.`
			);
		}

		const tabsViewed: any = {};

		const loadGridIntoAms = async (commit: boolean) => {
			let confirmText;

			if (commit) {
				const tabsNotViewedCount = Object.entries(tabsViewed).filter(tab => !tab[1]).length;
				const tabsViewedCount = Object.entries(tabsViewed).filter(tab => tab[1] === true).length;
				if (tabsNotViewedCount > 0) {
					playTabNotViewedAnimation();
					return await showWebixError(
						`Please review each grid before loading them into AMS. 
						You have viewed ${tabsViewedCount} out of ${tabsViewedCount + tabsNotViewedCount} tabs.`
					);
				}
				confirmText = `Load ${tabsViewedCount} grids into AMS now?`;
			} else {
				confirmText = `Validate grids?`;
			}

			await webix.confirm({
				title: "Confirm",
				ok: "Yes",
				cancel: "No",
				text: confirmText
			}).then(async () => {
				// load grids into ams now
				try {
					showLoadMessage('Validating grids...');

					// build up the list of grids to send to the loadNewGrids call
					const amsGridsToSend: AmsGridUnparsed[] = [];
					for (let amsGridIndex = 0; amsGridIndex < grids.length; amsGridIndex++) {
						const currentAmsGrid = grids[amsGridIndex];
						const amsGrid: AmsGridUnparsed = ({
							properties: [],
							tiers: []
						});

						// populate amsGrid.properties
						for (let i = 0; i < currentAmsGrid.properties.length; i++) {
							const currentProperty = currentAmsGrid.properties[i];
							const property = {
								key: currentProperty.key,
								value: currentProperty.value
							};
							amsGrid.properties.push(property);
						}

						// populate amsGrid.tiers
						for (let i = 0; i < currentAmsGrid.tiers.length; i++) {
							const currentTier = currentAmsGrid.tiers[i];
							const tier = {
								enrollments: Number(currentTier.enrollments),
								earnings: Number(currentTier.earnings),
								retention: Number(currentTier.retention),
								second_usage: Number(currentTier.second_usage),
								invoice_submission: Number(currentTier.invoice_submission),
								valid_email: Number(currentTier.valid_email),
								tier: Number(`${currentTier.tier}`.split(' ')[1])
							}
							amsGrid.tiers.push(tier);
						}

						amsGridsToSend.push(amsGrid);
					}

					// load the grids without committing to validate them
					let result = await loadNewGridsMutation({variables: {grids: amsGridsToSend, noCommit: true, etc}});
					let response: LoadNewGridsResponse = result?.data?.loadNewGrids;

					// if validation was successful, and we want to commit, run the call again without the final rollback
					if (commit && response?.success) {
						showLoadMessage('Loading grids...');
						result = await loadNewGridsMutation({variables: {grids: amsGridsToSend, noCommit: false, etc}});
						response = result?.data?.loadNewGrids;
					}

					// display any errors if applicable
					if (!response) {
						return showWebixError("An unexpected error has occurred while loading the grids into AMS");
					}
					if (response.error) {
						tryFocusTab(response.error);
						return showWebixError(`Failed to load grids into AMS:<br/><br/><i>${webix.template.escape(response.error)}</i>`);
					}
					if (!response.success) {
						return showWebixError(`Load failed unexpectedly`);
					}

					// load was a success
					showLoadMessage(false);
					const loadedGrids = response?.grids;
					if (!loadedGrids) {
						return showWebixError(`No grids loaded from response`);
					}

					let successMessage;
					if (commit) {
						successMessage =
							`Loaded new grid into AMS:<br/>
							<ul>
								${loadedGrids.map((grid: any) => `<li>${webix.template.escape(grid.properties.name)}</li>`).join('')}
							</ul>
							`
					} else {
						successMessage = `The grids have been validated and can be loaded into AMS.`;
					}
					await webix.alert({
						title: "Success",
						text: successMessage
					});
					(document as any).querySelector('body.webix_modalbox_inside')?.classList?.remove('webix_modalbox_inside');
					if (commit) {
						setGridManagerState(GridManagerState.UPLOAD_FILES);
					}
				} catch (error) {
					logPossibleApolloError(error);
					return showWebixError(`An unexpected error has occurred: ${webix.template.escape(error)}`);
				}
			}).fail(() => {
				// user cancelled submission
			});
		}

		const cancelButton = {
			view: "button",
			type: "icon",
			icon: "wxi-close",
			label: "Cancel",
			width: 200,
			align: "center",
			css: "webix_danger",
			click: () => {
				setGridManagerState(GridManagerState.UPLOAD_FILES);
				setWebixUiConfig(null);
			}
		};

		const validateButton = {
			view: "button",
			type: "icon",
			icon: "wxi-eye",
			label: "Validate",
			width: 200,
			align: "center",
			css: "webix_secondary",
			click: async () => {
				loadGridIntoAms(false);
			}
		};

		const submitButton = {
			view: "button",
			type: "icon",
			icon: "wxi-check",
			label: "Submit",
			width: 200,
			align: "center",
			css: "webix_primary",
			click: async () => {
				loadGridIntoAms(true);
			}
		};

		const pleaseReviewPrompt = {
			view: "label",
			label: "Please review all of the grids below before submitting",
			align: "center"
		}

		const webixUi: any = {
			view: "scrollview",
			id: "scrollview",
			scroll: "y",
			body: {
				rows: []
			}
		}

		// cancel and submit buttons at the top
		webixUi.body.rows.push({
			view: "toolbar",
			cols: [pleaseReviewPrompt, cancelButton, validateButton, submitButton]
		});

		// each grid is in a tab, track them to make sure they are all viewed before submitting
		// first tab is marked as view on render
		const tabView: any = {
			view: "tabview",
			id: "gridTabView",
			cells: [],
			tabbar: {
				on: {
					onAfterRender: () => {
						document.querySelectorAll('.webix_item_tab').forEach((node: any) => {
							const buttonId = node.getAttribute('button_id');
							if (tabsViewed[buttonId] === undefined) {
								if (Object.keys(tabsViewed).length === 0) {
									tabsViewed[buttonId] = true;
								} else {
									tabsViewed[buttonId] = false;
								}
							}
							node.setAttribute('tab_viewed', (tabsViewed[buttonId] ? '1' : '0'));
						});
					},
					onAfterTabClick: (id: any, ev: any) => {
						const tab: any = document.querySelector(`.webix_item_tab[button_id='${id}']`);
						tab.setAttribute('tab_viewed', '1');
						tabsViewed[id] = true;
					}
				}
			}
		}

		webixUi.body.rows.push(tabView);

		// format number as ###,###.## and include % or $ based on type, handles both string and number type cells
		const formatNumericCell = (cell: any, type: '$' | '%' | 'Number'): string => {
			if (typeof cell === 'number') {
				cell = cell.toLocaleString(undefined, {minimumFractionDigits: 2});
			} else if (typeof cell === 'string') {
				cell = Number(cell).toLocaleString(undefined, {minimumFractionDigits: 2});
			}
			if (type === '$') {
				cell = '$' + cell;
			} else if (type === '%') {
				cell = cell + '%';
			}
			return cell;
		}

		for (let amsGridIndex = 0; amsGridIndex < grids.length; amsGridIndex++) {
			const currentAmsGrid = grids[amsGridIndex];

			// pull tier requirement headers
			const tableColumns = [
				{
					id: "tier",
					header: "Tier",
					fillspace: true,
					format: (cell: string | null) => `${cell ? cell.split(' ')[1] : cell}`
				},
				{
					id: "enrollments",
					header: "Active Customers",
					fillspace: true
				},
				{
					id: "earnings",
					header: "Earnings",
					fillspace: true,
					format: (cell: any) => formatNumericCell(cell, '$')
				},
				{
					id: "retention",
					header: "Retention",
					fillspace: true,
					format: (cell: any) => formatNumericCell(cell, '%')
				},
				{
					id: "second_usage",
					header: "Second Usage",
					fillspace: true,
					format: (cell: any) => formatNumericCell(cell, '%')
				},
				{
					id: "valid_email",
					header: "Valid Email Address",
					fillspace: true,
					format: (cell: any) => formatNumericCell(cell, '%')
				},
				{
					id: "invoice_submission",
					header: "Invoice Submission",
					fillspace: true,
					format: (cell: any) => formatNumericCell(cell, '%')
				}
			];

			// pull tier requirement rows
			const tableData = currentAmsGrid.tiers;
			const gridName = currentAmsGrid.properties.filter((prop: any) => prop.key === 'Name').map((prop: any) => prop.value)[0];

			const amsGridView: any = {
				view: "scrollview",
				body: {
					rows: []
				}
			};

			tabView.cells.push({
				header: gridName,
				body: amsGridView
			});

			// get grid properties
			const gridPropertiesView: any = ({
				view: "property",
				id: "sets",
				nameWidth: 180,
				autoheight: true,
				elements: [
					{label: "Grid Properties", type: "label"}
				]
			});

			for (let i = 0; i < currentAmsGrid.properties.length; i++) {
				const key: string = currentAmsGrid.properties[i].key;
				const value: string = currentAmsGrid.properties[i].value;
				gridPropertiesView.elements.push({label: key, value: value});
			}

			// grid properties after
			amsGridView.body.rows.push(gridPropertiesView);

			// populate tier table at bottom
			amsGridView.body.rows.push({
				view: "datatable",
				columns: tableColumns,
				data: tableData,
				readonly: true,
				rowHeight: 24,
				headerRowHeight: 24,
				autoheight: false,
				scrollAlignY: false
			});
		}

		setWebixUiConfig(webixUi);
		setGridManagerState(GridManagerState.REVIEW_GRIDS);
		showLoadMessage(false);
	}

	return (
		<IonPage>
			{!embedded &&
				<IonHeader>
					<IonToolbar>
						<IonButtons slot="secondary">
							<IonBackButton icon={arrowBack} text="" className="custom-back"/>
						</IonButtons>
						<IonTitle>Grid Manager</IonTitle>
					</IonToolbar>
				</IonHeader>
			}
			<IonLoading isOpen={isLoading} message={loadingMessage}/>
			<IonContent fullscreen className="grid-manager">
				{/* Uploading files UI */}
				{(gridManagerState === GridManagerState.UPLOAD_FILES) &&
					<div className="grid-manager-details">
						<IonCard className="grid-manager-primary-card">
							<IonCardHeader>
								<IonCardTitle>AMS Grid Loader</IonCardTitle>
								<IonCardSubtitle>Upload new grids to AMS</IonCardSubtitle>
							</IonCardHeader>
							<IonCardContent>
								<IonText>
									<p>
										This tool will allow you to upload new grids on AMS. After loading, they will be made available
										to select using the Admin Edit Tool. These grids can be assigned directly to enrollment reps, or
										made available for masters and managers to select during onboarding. By default, these grids
										will be loaded as active grids that are not available to any master/manager and have no users
										assigned to them.
									</p>
									<p>
										To load a grid, download the newest version of the template using the <i>"Download
										Template"</i> button, and fill it out accordingly. After you have your files ready, you can upload
										them using the <i>"Upload Files"</i> button. You can select up to 5 grids to load at once. After
										uploading, you will be given a chance to review and validate each of the grids before
										submitting. <b>Grids will not be made available until you have reviewed the files and submitted them
										after uploading the files.</b>
									</p>
									<p>
										Please ensure that the grids are correct before loading. If an incorrect grid is loaded into AMS,
										correct the template and re-upload with a newer version number. Deactivating grids will require
										submitting a ticket to the development team.
									</p>
								</IonText>
								<IonList>
									<IonRow>
										{/* this event is so both the label and the button trigger an upload event, without doubling up */}
										<IonButton id="fileButton" fill="solid" size="large" color="primary" onClick={(e) => {
											e.stopPropagation();
											(document as any).getElementById('file').click(false);
											return false;
										}}>
											<IonIcon icon={cloudUploadOutline} slot="start"></IonIcon>
											<input id="file" multiple={true} hidden type="file"
											       accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
											       onChange={onFilesSelected}
											       onClick={(event) => {
												       event.currentTarget.value = ''
											       }}/>
											<span>Upload Files</span>
										</IonButton>
									</IonRow>
									<IonRow>
										<IonButton fill="solid" size="large" color="secondary"
										           href="https://paygo-ams-public.s3-us-west-2.amazonaws.com/PayGridTemplate_Clean.xlsx">
											<IonIcon icon={downloadOutline} slot="start"/>
											Download Template
										</IonButton>
									</IonRow>
								</IonList>
							</IonCardContent>
						</IonCard>
					</div>
				}
				{/* Reviewing grids UI */}
				{(gridManagerState === GridManagerState.REVIEW_GRIDS) &&
					<Webix ui={webixUiConfig}/>
				}
			</IonContent>
		</IonPage>
	);
};

export default GridManager;